import { plural } from "../plural";

/** English strings for the app's settings area. Keys are camelCase, grouped by screen. */
export default {
  /** "120 / 3,000" under a text box. */
  charCount: "{count} / {max}",

  account: {
    title: "Account settings",
    subtitle: "Manage your profile, security, and account.",
    copiedLabel: "{label} copied.",
    copyFailed: "Couldn't copy.",
    notifyOn: "Notifications on. We'll tell you when a build finishes.",
    notifyDenied: "That wasn't allowed. You can still turn it on from your device settings.",
    notifyOpenBrowser: "Open this site's settings in your browser to allow notifications.",
    nameEmpty: "Name can't be empty.",
    nameUpdated: "Name updated.",
    nameFailed: "Couldn't update name.",
    passwordUpdated: "Password updated.",
    passwordFailed: "Couldn't update password.",
    photoNotImage: "Choose an image file.",
    photoTooBig: "Max photo size is 2MB.",
    photoUpdated: "Profile photo updated.",
    photoFailed: "Couldn't upload photo.",
    photoRemoved: "Profile photo removed.",
    photoRemoveFailed: "Couldn't remove photo.",
    deletePasswordNeeded: "Enter your password to delete this account.",
    deleteFailed: "Couldn't delete account.",
    memoryOnEmpty: "On · nothing remembered yet",
    memoryOnCount: plural({ one: "On · {n} memory", other: "On · {n} memories" }),

    profileTitle: "Profile",
    profileDescription: "Your identity on Mythex.",
    uploadPhotoAria: "Upload profile photo",
    profilePhoto: "Profile photo",
    photoHint: "JPEG, PNG, WebP, or GIF · max 2MB",
    uploading: "Uploading…",
    name: "Name",
    change: "Change",
    namePlaceholder: "Your name",
    saveName: "Save name",
    email: "Email",

    planTitle: "Plan",
    planDescription: "What you are on, and what is left on it.",
    plan: "Plan",
    credits: "Credits",

    securityTitle: "Security",
    securityDescription: "Protect your account with a strong password.",
    password: "Password",
    passwordHint: "At least 8 characters.",
    currentPassword: "Current password",
    newPassword: "New password",
    updating: "Updating…",
    updatePassword: "Update password",

    notificationsTitle: "Notifications",
    notificationsDescription: "Be told when a build finishes, even when Mythex isn't open.",
    notifications: "Notifications",
    asking: "Asking…",

    memoryTitle: "Memory",
    memoryDescription: "What the agent knows about you across projects — see it, edit it, or turn it off.",
    memory: "Memory",

    mcpTitle: "MCP / Claude",
    mcpDescription:
      "Paste the MCP URL in Claude or Cursor → Connect → sign in on Mythex. Tools need Pro + credits.",
    mcpSteps:
      "<b>1.</b> Copy this URL into Claude (custom connector) or Cursor (MCP server URL). <b>2.</b> Click <b>Connect</b> — your browser opens Mythex to approve access via OAuth.",
    mcpUrl: "MCP URL",
    mcpFreeNote: "You can Connect on Free. Sandbox tools require <pro>Pro</pro>.",
    mcpBilling:
      "Claude pays for the model. Sandbox time is included with Pro; Mythex credits pay for hosting. Docs: <guide>MCP guide</guide>.",

    dangerTitle: "Danger zone",
    dangerDescription: "Irreversible actions. Proceed with caution.",
    deleteAccount: "Delete account",
    deleteAccountHint: "Permanently delete your account and all projects.",
    deleteConfirm: "This cannot be undone. Type {word} to confirm.",
    deleteMyAccount: "Delete my account",
  },

  memory: {
    saved: "Saved.",
    saveFailed: "Couldn't save that. Try again.",
    alreadyRemembered: "Already remembered.",
    addFailed: "Couldn't add that memory.",
    editFailed: "Couldn't save that memory.",
    deleteFailed: "Couldn't delete that memory.",
    allDeleted: "All memories deleted.",
    clearFailed: "Couldn't delete your memories.",

    back: "Back to Account settings",
    title: "Memory",
    intro: "What the Mythex agent knows about you, in every project you build. It only ever knows what's on this page.",
    loadFailed: "Couldn't load your memory.",

    settingsDescription: "Turn memory off and the agent neither reads nor saves anything about you.",
    useMemory: "Use memory",
    useMemoryHint: "Let the agent use what's below, and save new things when you ask it to remember.",
    learnFromChats: "Learn from my chats",
    learnFromChatsHint:
      "Pick up lasting facts from what you write — never from files, websites or the agent's own replies.",
    searchChats: "Search past chats",
    searchChatsHint: "Let the agent look through your earlier chats when you refer back to them.",

    aboutTitle: "About you",
    aboutDescription: "Who you are and how you like to work. The agent reads this at the start of every chat.",
    aboutPlaceholder:
      "e.g. I run a small bakery in Amman. Reply in Arabic, keep it simple — I'm not technical.\nOur brand colors are cream and deep green.",

    listTitle: "What Mythex remembers",
    listDescription: "Short facts learned in your chats or added by you — newest first. {count} of {max}.",
    addPlaceholder: "Add something for the agent to remember…",
    filter: "Filter",
    empty: "Nothing yet. Tell the agent \"remember that…\" in any chat, or add something above.",
    noMatch: "No memory matches that.",
    deleteAll: "Delete all memories",
    footer:
      "Memories never hold passwords or keys, and aren't learned about health, religion, politics or similar topics unless you ask. Knowledge for a single project lives in that project's settings; for a whole workspace, in any of its projects' settings (owners only).",

    confirmTitle: "Delete all memories?",
    confirmSubtitle: "The agent will forget everything on this list. Your About you text and settings stay.",
    confirmLabel: "Delete all",

    addedByYou: "Added by you",
    savedWhenAsked: "Saved when you asked",
    savedWhenAskedIn: "Saved when you asked in {project}",
    learnedFromChat: "Learned from a chat",
    learnedFromChatIn: "Learned from a chat in {project}",
  },

  knowledge: {
    saved: "Saved. The agent uses it from your next message.",
    saveFailed: "Couldn't save. Try again.",
    loadFailed: "Couldn't load knowledge.",
    projectTitle: "Project knowledge",
    projectDescription:
      "Everything the agent should always know about this app — audience, brand, rules, things to leave alone. Read on every message in this project, by everyone who builds in it.",
    projectPlaceholder:
      "e.g. Customers are restaurant owners in the Gulf; the app is Arabic-first (RTL).\nPrices are in AED. Never change the checkout flow without asking.",
    personalTitle: "Knowledge for all your projects",
    workspaceTitle: "Workspace knowledge · {name}",
    personalDescription: "Applies to every project in your personal workspace.",
    workspaceDescription: "Applies to every project in this workspace, for everyone in it.",
    workspacePlaceholder:
      "e.g. Company: Acme Foods. Brand colors #0F766E and #F5F0E6, font Inter. Use Stripe for payments.",
    readOnlyNote: "Only the workspace owner can change this.",
    memoryPointer: "What the agent remembers about <em>you</em>, across every project, is in <link>Settings → Memory</link>.",
  },

  project: {
    dialogTitle: "Project settings",
    dialogSubtitle: "Name, knowledge, database, storage, secrets, export, GitHub, and mobile app.",
    sectionAria: "Project settings section",
    untitled: "Untitled",

    tabGeneral: "General",
    tabKnowledge: "Knowledge",
    tabSecrets: "Secrets",
    tabExport: "Export",
    connectors: "Connectors",

    renamed: "Project renamed.",
    renameFailed: "Couldn't rename the project.",
    projectName: "Project name",

    featureDatabase: "Database",
    featureStorage: "App storage",
    featureExport: "Export / download",
    featureGithub: "GitHub sync",

    database: "Database",
    fileStorage: "File storage",
    usage: "Usage",
    // Usage tab: this project's credits, its monthly limit, its database setting.
    usageLoadFailed: "Couldn't load usage.",
    usageLast30: "Last 30 days",
    usageAllProjects: "Usage for all projects",
    limitTitle: "Monthly limit",
    limitText:
      "Caps this app's running costs: its database, hosting and file storage. At the limit we pause the published app until the 1st, or until you raise the limit. Nothing is deleted.",
    limitThisMonth: "{spent} of {limit} credits used this month",
    limitNoneThisMonth: "{spent} credits used this month · no limit",
    limitPlaceholder: "Credits a month",
    limitSet: "Set limit",
    limitRemove: "Remove limit",
    limitSaved: "Limit saved.",
    limitRemoved: "Limit removed.",
    limitInvalid: "Enter a whole number of credits, 1 or more.",
    limitWarn: "Over 80% of this month's limit.",
    limitReached: "Limit reached: the published app is paused.",
    ownerOnly: "Only the project owner can change this.",
    dbModeTitle: "Database",
    dbSleepTitle: "Sleep when idle",
    dbSleepText: "Sleeps after a few idle minutes and wakes on the next visit in a moment. You only pay while it's awake.",
    dbAlwaysOnTitle: "Always on",
    dbAlwaysOnText: "Never sleeps, so there is no wake-up pause. About {credits} credits a month or more, every month.",
    dbAlwaysOnPro: "Always on is available on Pro.",
    dbModeSaved: "Database setting saved.",
    saveFailed: "Couldn't save. Try again.",

    detailsTitle: "Details",
    detailsLoadFailed: "Couldn't load project details.",
    detailProjectId: "Project ID",
    detailCreated: "Created",
    detailLastActive: "Last active",
    detailLiveUrl: "Live app",
    detailNotPublished: "Not published",
    detailWorkspace: "Workspace",
    detailPersonalWorkspace: "Personal",

    // Your data: take the database or the files with you.
    dbEmptyTitle: "No database yet",
    dbEmptyText: "Mythex adds one when your app needs to save data.",
    storageEmptyTitle: "No file storage yet",
    storageEmptyText: "Mythex turns it on when your app needs uploads.",
    filesEmptyText: "Files your app stores, like uploads, show up here.",
    dbDownloadBackup: "Download backup",
    dbPreparingBackup: "Preparing backup…",
    dbBackupText: "A full copy of your app's data as one file. It restores into any Postgres database.",
    dbBackupFailed: "Couldn't make the backup. Try again.",
    dbConnect: "Connect from another tool",
    dbShowConnection: "Show connection string",
    dbConnectionLabel: "Connection string",
    dbConnectionHint:
      "Paste it into TablePlus, DBeaver, psql or another host's import to view, export or move your data. Anyone with it can read and change your app's data, including your live app's.",
    dbConnectionFailed: "Couldn't load the connection string.",
    dbWhichLabel: "Database",
    dbDev: "Development",
    dbLive: "Live",
    dbDevHint: "What the preview and the agent use while you build.",
    dbLiveHint: "Your published app’s own data. Publishing brings schema changes over; its rows stay its own.",
    dbResetPassword: "Reset password",
    dbResetTitle: "Reset the database password?",
    dbResetText:
      "The current connection string stops working right away. Your preview and live app get the new one automatically; the live app restarts, which takes a few seconds.",
    dbResetDone: "Password reset. The new connection string is below.",
    dbResetLiveFailed: "Password reset, but the live app couldn't be updated. Publish again to fix it.",
    dbResetFailed: "Couldn't reset the password. Try again.",
    show: "Show",
    hide: "Hide",
    storageDownloadAll: "Download all",
    storagePreparingZip: "Preparing zip…",
    storageZipFailed: "Couldn't make the zip. Try again.",
    storageZipTooLarge: "Too much to zip at once (over 5 GB). Download files one by one.",
    storageNoFiles: "No files yet.",
    filesTitle: "Files",
    filesLoadFailed: "Couldn't load files.",
    filesSummary: plural({ one: "{n} file · {size}", other: "{n} files · {size}" }),
    filesSearch: "Search files",
    filesNoMatch: "No files match “{q}”.",
    filesColName: "Name",
    filesColSize: "Size",
    filesColModified: "Modified",
    filesRange: "{from}–{to} of {total}",
    filesPrev: "Previous page",
    filesNext: "Next page",
    fileOpen: "Open",
    fileDownload: "Download",
    fileDelete: "Delete",
    fileDeleteTitle: "Delete this file?",
    fileDeleteText: "{name} is removed from your app's storage. Anything in your app that shows it will stop showing it.",
    fileDeleted: "File deleted.",
    fileDeleteFailed: "Couldn't delete the file.",

    deleteHint: "Delete this project permanently. Published apps go offline.",
    deleteProject: "Delete project",

    exportTitle: "Export codebase",
    exportText:
      "Download a <code>.tar.gz</code> of your project files. Secrets and <code>node_modules</code> are excluded — safe to move between environments.",
    preparing: "Preparing…",
    downloadExport: "Download export",
    downloadStarted: "Download started.",
    exportFailed: "Could not export project.",
    importTitle: "Import into this project",
    importText:
      "Replace this project's files with an export (or zip). Existing secrets stay; imported <code>.env</code> files are stripped.",
    importing: "Importing…",
    chooseArchive: "Choose archive",
    imported: "Imported — AI is setting up the project.",
    importFailed: "Import failed.",

    githubConnected: "GitHub connected.",
    popupBlocked: "Pop-up blocked — allow pop-ups for Mythex, or try again.",
    githubAuthInWindow: "Complete GitHub authorization in the new window.",
    githubDisconnected: "GitHub disconnected.",
    githubDisconnectFailed: "Could not disconnect GitHub.",
    tokenFailed: "Could not connect GitHub token.",
    linkedEmpty:
      "Linked, but that branch is empty on GitHub (earlier push may have wiped it). Switch to a branch that has your app code, then Pull again.",
    repoPulled: "Repo pulled into the sandbox — AI is setting it up.",
    repoLinkedPushed: "Repo linked and pushed to GitHub.",
    linkFailed: "Could not link repo.",
    pushed: "Pushed to GitHub.",
    syncFailed: "Sync failed.",
    pulledEmpty: "Pulled, but that branch has no source files. Pick a branch with your app code.",
    pulled: "Pulled from GitHub — AI is setting it up.",
    pullFailed: "Pull failed.",
    prFailed: "Could not open PR link.",

    connectGithub: "Connect GitHub",
    connectedAs: "Connected as {login}.",
    disconnecting: "Disconnecting…",
    connectGithubText:
      "Connect your GitHub account to sync this project to a repository. A new window opens for authorization; this settings panel stays open.",
    noOauth: "OAuth App not configured. Paste a personal access token with <code>repo</code> scope (local/dev).",
    connecting: "Connecting…",
    saveToken: "Save token",
    linkTitle: "Link repository",
    linkText:
      "Pull code from the branch you choose (e.g. main). Mythex writes changes to a separate working branch so your base branch stays untouched. Open a PR when you want to merge.",
    yourRepos: "Your repositories",
    selectRepo: "Select a repo…",
    repoPrivate: "{name} (private)",
    repoLabel: "Repo (owner/name)",
    baseBranchLabel: "Pull from (base branch)",
    workingBranchLabel: "Mythex branch (commits / sync)",
    branchHint: "Sync and auto-sync only push to the Mythex branch — never to your base branch.",
    createRepo: "Create repo if it doesn't exist",
    linking: "Linking…",
    pulling: "Pulling…",
    updateLink: "Update link",
    linkPush: "Link & push",
    linkPull: "Link & pull",
    syncing: "Syncing…",
    syncNow: "Sync now",
    pull: "Pull",
    openPr: "Open PR",
    linkedTo: "Linked to {repo} · pull {base} → push {branch}",

    secretsIntro:
      "API keys and other secrets Mythex injects into your app at runtime. Managed database and storage credentials are added automatically when needed — not listed here. Your app's own <code>.env</code> in Code is separate and not overwritten by Save.",
    valuePlaceholder: "value",
    hideValue: "Hide value",
    showValue: "Show value",
    removeVariable: "Remove variable",
    addVariable: "Add variable",
    envSaveFailed: "Couldn't save the environment variables.",
    confirmSaveTitle: "Save secrets?",
    confirmRemoveOne: "“{key}” will be deleted from this project.",
    confirmRemoveMany: plural({
      one: "{n} secret will be deleted from this project.",
      other: "{n} secrets will be deleted from this project.",
    }),
  },

  mcp: {
    saveFailed: "Couldn't save that server.",
    approveOn: "Approve Mythex on {name}, then come back.",
    serverSaved: "{name} saved.",
    serverConnected: "Server connected.",
    signInFailed: "That sign-in didn't finish.",
    connectedWithTools: plural({ one: "{name} connected — {n} tool.", other: "{name} connected — {n} tools." }),
    connectedNoTools: "{name} connected.",
    unreachable: "Could not reach that server.",
    connectFailed: "Couldn't start that connection.",
    refreshed: plural({ one: "{name} — {n} tool.", other: "{name} — {n} tools." }),
    changeFailed: "Couldn't change that server.",
    removed: "{name} removed.",
    removeFailed: "Couldn't remove that server.",

    serverName: "Server name",
    namePlaceholder: "My MCP server",
    serverUrl: "Server URL",
    authentication: "Authentication",
    oauthLabel: "OAuth (default)",
    oauthHint: "Authorize in the next step.",
    bearerLabel: "Bearer token or API key",
    bearerHint: "Use a bearer token or API key if the MCP server doesn't support OAuth.",
    tokenStored: "Stored — leave blank to keep it",
    tokenPlaceholder: "Paste the token",
    noneLabel: "No authentication",
    noneHint: "Connect without credentials if the server doesn't require authentication.",
    credentialsNote:
      "Credentials are held encrypted by Mythex. They are never written into your code and never reach the sandbox your app builds in.",
    connecting: "Connecting…",
    saveAuthorize: "Save & authorize",
    addAuthorize: "Add & authorize",

    title: "Custom MCP",
    description:
      "Connect any MCP server by URL — your own, your company's, or one a vendor gave you. Its tools show up to the agent alongside the catalogue.",
    addServer: "Add server",
    emptyTitle: "No MCP servers yet",
    emptyText: "Add one to give the agent tools that aren't in the catalogue.",
    waitingApproval: "Waiting for you to approve access.",
    tools: plural({ one: "{n} tool", other: "{n} tools" }),
    signedInSuffix: " · signed in",
    tokenSuffix: " · token",
    offSuffix: " · off",
    opening: "Opening…",
    trying: "Trying…",
    editAria: "Edit {name}",
    refreshAria: "Refresh {name}",
    refreshTitle: "Re-read the tool list",
    removeAria: "Remove {name}",

    mythexTitle: "Use Mythex from Claude, Cursor or Codex",
    mythexText:
      "Mythex serves an MCP endpoint too. Add this URL in your client as a custom connector, then approve the connection on Mythex.",
    docs: "Docs",

    removeTitle: "Remove {name}?",
    removeOauthText: "The agent loses its tools, and adding it back means signing in again.",
    removeOtherText: "The agent loses its tools, and adding it back means entering the details again.",
    removing: "Removing…",
  },
};
